import AiStudioMenuIcon from 'assets/images/others/ai-studio.svg';
import DamMenuIcon from 'assets/images/others/dam.svg';
import ConfiguratorMenuIcon from 'assets/images/others/product-configurator.svg';
import CustomizerMenuIcon from 'assets/images/others/visual-customizer.svg';
import React, { FC, FunctionComponent, Suspense, useEffect, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { backofficeUrl, T } from 'src/shared/helpers';
import { EnvType, useLastUsedTool } from 'src/shared/hooks';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import { useUsetifulElementObserver } from 'src/shared/hooks/useUsetifulElementObserver';
import LogoZakeke from 'src/shared/svg/logoZakeke';
import styled from 'styled-components';
import { useAppState } from '../../../shared/appState';
import config from '../../../shared/config';
import { BackButton } from '../buttons';
import { toolbarMenuFragment$key } from './__generated__/toolbarMenuFragment.graphql';
import { ContainerLogo, ContainerToolbar, LinkLogo, MainContainerToolbar } from './globalsToolbar';
import { closeHeadwayWidget } from './headwayWidget';
import LeftMenu from './LeftMenu';
import RightMenu from './rightMenu';
import { setWidgetFalse as hideZendeskWidget } from './widgetZenDesk';
import { ZendeskAPI } from './widgetZenDeskHelp';

// Function to generate tools array based on feature flag
export const getTools = (isNewConfiguratorBackoffice: boolean) =>
    [
        {
            type: 'CUSTOMIZER',
            name: T._('Visual Product Customizer', 'Admin'),
            description: T._('Automate your printing or engraving process', 'Admin'),
            icon: <CustomizerMenuIcon />,
            isAI: false,
            isNew: false,
            isEarlyAccess: false,
            url: backofficeUrl('/Dashboard?from=customizer')
        },
        {
            type: 'CONFIGURATOR',
            name: T._('3D Product Configurator', 'Admin'),
            description: T._('Unique 3D shopping experience', 'Admin'),
            icon: <ConfiguratorMenuIcon />,
            isAI: false,
            isNew: false,
            isEarlyAccess: false,
            url: `${config.culture}/Composer?from=customizer`
        },
        {
            type: 'VIEWER',
            name: T._('3D DAM & AR Viewer', 'Admin'),
            description: T._('Manage, collaborate & share 3D/AR models', 'Admin'),
            icon: <DamMenuIcon />,
            isAI: false,
            isNew: false,
            isEarlyAccess: false,
            url: backofficeUrl('/DAM?page=/gallery')
        },
        {
            type: 'AI_STUDIO',
            name: T._('AI Agents Studio', 'Admin'),
            description: T._('Bring your products to life with AI-powered tools.', 'Admin'),
            icon: <AiStudioMenuIcon />,
            isAI: false,
            isNew: true,
            isEarlyAccess: true,
            url: backofficeUrl('/ai-creative-tools/studio')
        },
        ...(isNewConfiguratorBackoffice
            ? [
                  {
                      type: 'CONFIGURATOR_2',
                      name: T._('PC (backoffice unico)', 'Admin'),
                      description: T._('Unique 3D shopping experience', 'Admin'),
                      icon: <ConfiguratorMenuIcon />,
                      isAI: false,
                      isNew: true,
                      isEarlyAccess: true,
                      url: backofficeUrl('/Dashboard?from=configurator2')
                  }
              ]
            : [])
    ] as const;

export interface IToolbar {
    isMenuHidden: boolean;
    forceTools: boolean;
    showBackButton?: boolean;
    isGuest?: boolean;
}

export interface ItemMenu {
    name: string;
    urlPage: string;
    icon: string;
    tooltip: string;
    helpKeywords: string[];
    menu: ItemMenu[];
}

const BackAndLogoContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 44px;
    align-items: center;
`;

const Toolbar: FunctionComponent<IToolbar> = (props) => {
    const isGuest = Boolean(window.location.pathname.indexOf('/Admin') === -1 || props.isGuest);
    const isAuthenticated = !!config.token && !isGuest;

    // Observe Usetiful banner and adjust toolbar position accordingly
    useUsetifulElementObserver('#usetiful-top-banner', (elObserved?: HTMLElement) => {
        if (props.isMenuHidden) return;

        const element = document.querySelector('#toolbar') as HTMLElement;
        const mainContainer = document.querySelector('body.new-backoffice') as HTMLElement;
        const leftSidebar = document.querySelector('#main-left-sidebar') as HTMLElement;

        const elementHeight = element.offsetHeight;
        mainContainer.style.paddingTop = `${elementHeight}px`;
        if (leftSidebar) {
            leftSidebar.style.height = `calc(100% - ${elementHeight}px)`;
            leftSidebar.style.top = `${elementHeight}px`;
        } else {
            // wait for leftSidebar to be rendered
            const interval = setInterval(() => {
                const leftSidebar = document.querySelector('#main-left-sidebar') as HTMLElement;
                if (leftSidebar) {
                    leftSidebar.style.height = `calc(100% - ${elementHeight}px)`;
                    leftSidebar.style.top = `${elementHeight}px`;
                    clearInterval(interval);
                }
            }, 100);
        }

        if ((window as any).zE) {
            (window as any).zE('webWidget', 'updateSettings', {
                webWidget: {
                    offset: {
                        vertical: `${elementHeight - 60}px`
                    }
                }
            });
        } else {
            console.warn('Zendesk is not initialized yet');
            const interval = setInterval(() => {
                if ((window as any).zE) {
                    (window as any).zE('webWidget', 'updateSettings', {
                        webWidget: {
                            offset: {
                                vertical: `${elementHeight - 60}px`
                            }
                        }
                    });
                    clearInterval(interval);
                }
            }, 500);
        }
    });

    return (
        <MainContainerToolbar id='toolbar' data-element-id='toolbar' $visitorMode={isGuest}>
            <div id='usetiful-top-banner'></div>
            <ContainerToolbar $visitorMode={isGuest}>
                <BackAndLogoContainer>
                    {props.showBackButton && <BackButton>{T._('Back', 'Admin')}</BackButton>}
                    <ContainerLogo>
                        <LinkLogo href={isGuest ? 'https://www.zakeke.com/home' : backofficeUrl('/Dashboard')}>
                            <LogoZakeke />
                        </LinkLogo>
                    </ContainerLogo>
                </BackAndLogoContainer>
                <Suspense fallback={<FakeLeftAndRightMenu isMenuHidden={props.isMenuHidden} forceTools={props.forceTools} isVisitorMode={isGuest} />}>
                    {!isAuthenticated && <FakeLeftAndRightMenu isMenuHidden={props.isMenuHidden} forceTools={props.forceTools} isVisitorMode={isGuest} />}
                    {isAuthenticated && <RealLeftAndRightMenu isMenuHidden={props.isMenuHidden} forceTools={props.forceTools} isVisitorMode={isGuest} />}
                </Suspense>
            </ContainerToolbar>
        </MainContainerToolbar>
    );
};

const RealLeftAndRightMenu: FC<{ isMenuHidden: boolean; forceTools: boolean; isVisitorMode: boolean }> = ({ isMenuHidden, forceTools, isVisitorMode }) => {
    const { userInfo, ecommerceSettings } = useAccountData();
    const { queryData } = useAppState();

    const query = useFragment<toolbarMenuFragment$key>(
        graphql`
            fragment toolbarMenuFragment on Query {
                user {
                    userID
                }
                subscription {
                    tools {
                        isComposerEnabled
                        isViewerEnabled
                    }
                }
            }
        `,
        queryData!
    );

    // Feature flag for new configurator backoffice
    const isNewConfiguratorBackoffice = useFeatureFlagWithConditions('newConfiguratorBackoffice', {
        userId: (query?.user?.userID || userInfo?.userID || 1).toString()
    });

    // Generate tools array based on feature flag
    const toolsArray = getTools(isNewConfiguratorBackoffice);

    const [isUserPopupMenuVisible, setIsUserPopupMenuVisible] = useState(false);
    const [isLanguagePopupMenuVisible, setIsLanguagePopupMenuVisible] = useState(false);
    const [isSocialMenuVisible, setIsSocialMenuVisible] = useState(false);
    const [isToolPopupVisible, setIsToolPopupVisible] = useState(false);

    const navigate = useNavigate();
    const { setLastUsedTool } = useLastUsedTool();

    const closeAll = () => {
        setIsUserPopupMenuVisible(false);
        setIsLanguagePopupMenuVisible(false);
        setIsSocialMenuVisible(false);
        setIsToolPopupVisible(false);
        closeHeadwayWidget();
        hideZendeskWidget();
    };

    const goToBlog = () => {
        window.open('https://www.zakeke.com/blog/', '_blank');
    };

    const goToYourStore = (remoteUrl: string) => {
        window.open(remoteUrl, '_blank');
    };

    const handlerOnClick = async (link: string, type: EnvType) => {
        closeAll();

        if (isNewConfiguratorBackoffice) {
            const currentUrl = window.location.href;

            // Always redirect for CONFIGURATOR (classic) and VIEWER
            if (type === 'CONFIGURATOR') {
                if (!query.subscription?.tools?.isComposerEnabled) {
                    navigate(backofficeUrl('/landings/configurator'));
                } else {
                    window.location.href = link;
                }
                return;
            }

            if (type === 'VIEWER') {
                if (!query.subscription?.tools?.isViewerEnabled) {
                    navigate(backofficeUrl('/landings/viewer'));
                } else {
                    // Wait for the persistence mutation to complete BEFORE the full reload.
                    // Otherwise the browser may abort the in-flight request and the backend
                    // keeps the previous tool, making the switcher revert on the next page.
                    await setLastUsedTool(type);
                    window.location.href = link;
                }
                return;
            }

            if (type === 'AI_STUDIO') {
                navigate(backofficeUrl('/ai-creative-tools/studio'));
                return;
            }

            // For CUSTOMIZER and CONFIGURATOR_2, only redirect if IN one of the 4 specific contexts
            if (type === 'CUSTOMIZER' || type === 'CONFIGURATOR_2') {
                // Check if we're in one of the 4 specific contexts that should trigger redirect.
                // Compare lowercased — the DAM URL can come through as either /DAM or /dam.
                const lowerUrl = currentUrl.toLowerCase();
                const isInConfiguratorContext = lowerUrl.indexOf('landings/configurator') !== -1 || lowerUrl.indexOf('/composer') !== -1;
                const isInViewerContext = lowerUrl.indexOf('landings/viewer') !== -1 || lowerUrl.indexOf('/dam') !== -1;

                // Only redirect if we're IN one of the 4 specific contexts
                if (isInConfiguratorContext || isInViewerContext) {
                    await setLastUsedTool(type);
                    window.location.href = link;
                } else {
                    // Otherwise, just set the tool without redirecting (switch tool only)
                    setLastUsedTool(type);
                }
            }
        } else {
            // Original behavior when feature flag is disabled
            if (type === 'AI_STUDIO') {
                navigate(backofficeUrl('/ai-creative-tools/studio'));
            } else if (type === 'CONFIGURATOR' && !query.subscription?.tools?.isComposerEnabled) {
                navigate(backofficeUrl('/landings/configurator'));
            } else if (type === 'VIEWER' && !query.subscription?.tools?.isViewerEnabled) {
                navigate(backofficeUrl('/landings/viewer'));
            } else {
                window.location.href = link;
            }
        }
    };

    const handleLoaded = () => {
        ZendeskAPI('messenger', 'open');
    };

    useEffect(() => {
        const handleBodyClick = (e: MouseEvent) => {
            // se il click è dentro un dropdown, non chiudere
            // questo l'ho dovuto usare per lo switch menu per cui ho dovuto usare un portal
            // per fare in modo che il solo dropdown (non l'intero mainToolbar da cui proviene)
            // avesse lo z-index più alto rispetto ai tooltip sotto del menu laterale
            // al PortalPopupContainer ho aggiunto la classe tool-dropdown-popup
            const dropdowns = document.querySelectorAll('.tool-dropdown-popup');
            for (const dd of Array.from(dropdowns)) {
                if (dd.contains(e.target as Node)) return;
            }
            closeAll();
        };

        document.body.addEventListener('click', handleBodyClick);
        return () => document.body.removeEventListener('click', handleBodyClick);
    }, []);

    return (
        <>
            <LeftMenu
                isMenuHidden={isMenuHidden}
                forceTools={forceTools}
                userInfo={userInfo}
                ecommerceSettings={ecommerceSettings}
                closeAll={closeAll}
                setIsToolPopupVisible={setIsToolPopupVisible}
                isToolPopupVisible={isToolPopupVisible}
                handlerOnClick={handlerOnClick}
                goToBlog={goToBlog}
                setIsSocialMenuVisible={setIsSocialMenuVisible}
                isSocialMenuVisible={isSocialMenuVisible}
                setLastUsedTool={setLastUsedTool}
                userID={userInfo?.userID}
                toolsArray={toolsArray}
            />
            <RightMenu
                isFakeMenu={false}
                isMenuHidden={isMenuHidden}
                userInfo={userInfo}
                ecommerceSettings={ecommerceSettings}
                closeAll={closeAll}
                setIsUserPopupMenuVisible={setIsUserPopupMenuVisible}
                isUserPopupMenuVisible={isUserPopupMenuVisible}
                setIsLanguagePopupMenuVisible={setIsLanguagePopupMenuVisible}
                isLanguagePopupMenuVisible={isLanguagePopupMenuVisible}
                isVisitorMode={isVisitorMode}
                goToYourStore={goToYourStore}
                handleLoaded={handleLoaded}
            />
        </>
    );
};

// Build a Fake LeftAndRightMenu component with fake values
const FakeLeftAndRightMenu: FC<{ isMenuHidden: boolean; forceTools: boolean; isVisitorMode: boolean }> = ({ isMenuHidden, forceTools, isVisitorMode }) => {
    const fakeProps = {
        userInfo: { name: 'User', userID: 1 },
        ecommerceSettings: { currency: 'USD', ecommerceName: 'NONE' },
        additionalInfo: { isComposerEnabled: false, isViewerEnabled: false },
        closeAll: () => {},
        setIsToolPopupVisible: () => {},
        isToolPopupVisible: false,
        handlerOnClick: () => {},
        goToBlog: () => {},
        setIsSocialMenuVisible: () => {},
        isSocialMenuVisible: false,
        setIsUserPopupMenuVisible: () => {},
        isUserPopupMenuVisible: false,
        setIsLanguagePopupMenuVisible: () => {},
        isLanguagePopupMenuVisible: false,
        goToYourStore: () => {},
        handleLoaded: () => {},
        setLastUsedTool: () => {}
    };

    return (
        <>
            <LeftMenu
                isMenuHidden={isMenuHidden}
                forceTools={forceTools}
                userInfo={fakeProps.userInfo}
                ecommerceSettings={fakeProps.ecommerceSettings}
                closeAll={fakeProps.closeAll}
                setIsToolPopupVisible={fakeProps.setIsToolPopupVisible}
                isToolPopupVisible={fakeProps.isToolPopupVisible}
                handlerOnClick={fakeProps.handlerOnClick}
                goToBlog={fakeProps.goToBlog}
                setIsSocialMenuVisible={fakeProps.setIsSocialMenuVisible}
                isSocialMenuVisible={fakeProps.isSocialMenuVisible}
                setLastUsedTool={fakeProps.setLastUsedTool}
                userID={fakeProps.userInfo.userID}
                toolsArray={undefined} // Use default tools for fake menu
            />
            <RightMenu
                isFakeMenu
                isMenuHidden={isMenuHidden}
                userInfo={fakeProps.userInfo}
                ecommerceSettings={fakeProps.ecommerceSettings}
                closeAll={fakeProps.closeAll}
                setIsUserPopupMenuVisible={fakeProps.setIsUserPopupMenuVisible}
                isUserPopupMenuVisible={fakeProps.isUserPopupMenuVisible}
                setIsLanguagePopupMenuVisible={fakeProps.setIsLanguagePopupMenuVisible}
                isLanguagePopupMenuVisible={fakeProps.isLanguagePopupMenuVisible}
                isVisitorMode={isVisitorMode}
                goToYourStore={fakeProps.goToYourStore}
                handleLoaded={fakeProps.handleLoaded}
            />
        </>
    );
};

export default Toolbar;
